import React, { useEffect, useRef, useState } from "react";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase";
import { motion, useAnimation } from "framer-motion";
import "./GallerySection.css";

const SCROLL_DURATION = 30; // seconds for scroll speed

export default function GallerySection() {
  const [images, setImages] = useState([]);
  const trackRef = useRef(null);
  const controls = useAnimation();
  const [carouselWidth, setCarouselWidth] = useState(0);

  // Modal/lightbox state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImg, setModalImg] = useState(null);

  // Fetch images from Firebase Storage
  useEffect(() => {
    async function fetchGalleryImages() {
      try {
        const folderRef = ref(storage, "adminGallery");
        const result = await listAll(folderRef);
        const urls = await Promise.all(
          result.items.map((itemRef) => getDownloadURL(itemRef))
        );
        setImages(urls);
      } catch (err) {
        console.error("Failed to load gallery images:", err);
      }
    }
    fetchGalleryImages();
  }, []);

  // Double the images for infinite scroll effect
  const displayImages = [...images, ...images];

  // Track width for animation
  useEffect(() => {
    if (trackRef.current) {
      setCarouselWidth(trackRef.current.scrollWidth / 2);
    }
  }, [images]);

  // Animate the track (constant speed, no speed change on hover/modal)
  useEffect(() => {
    if (!carouselWidth || images.length === 0) return;
    controls.start({
      x: [-0, -carouselWidth],
      transition: {
        x: {
          repeat: Infinity,
          repeatType: "loop",
          duration: SCROLL_DURATION,
          ease: "linear",
        },
      },
    });
  }, [carouselWidth, images, controls]);

  // Modal/Lightbox handlers
  function openModal(imgSrc) {
    setModalImg(imgSrc);
    setModalOpen(true);
    document.body.style.overflow = "hidden";
  }
  function closeModal() {
    setModalOpen(false);
    setModalImg(null);
    document.body.style.overflow = "";
  }
  useEffect(() => {
    // ESC closes modal
    if (!modalOpen) return;
    function onKeyDown(e) {
      if (e.key === "Escape") closeModal();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [modalOpen]);

  if (images.length === 0) return null;

  return (
    <section className="gallery-section" aria-label="Farm Gallery">
      <div className="gallery-spacer">
        <h2 className="gallery-title">
          Golden Memories in the Making
        </h2>
        <p className="gallery-subtitle">
          At <strong>DeClerck’s Goldens</strong>, our gallery isn’t just about cute faces, it’s a glimpse into a puppy’s journey from playful beginnings to cherished family member.  
          <br />
          See the care, connection, and golden joy that define every moment on our farm.
        </p>
      </div>
      <motion.a
        href="/puppies"
        className="gallery-cta-btn"
        whileHover={{ scale: 1.07, boxShadow: "0 4px 22px #ffbf0099" }}
        whileTap={{ scale: 0.97 }}
        tabIndex={0}
      >
        Find Your Forever Friend
      </motion.a>
      <div
        className="gallery-carousel"
        aria-label="Golden Retriever Puppy Gallery"
        tabIndex={0}
      >
        <motion.div
          className="carousel-track"
          ref={trackRef}
          animate={controls}
          style={{ willChange: "transform" }}
        >
          {displayImages.map((src, i) => (
            <div
              className="carousel-item"
              key={i}
              onClick={() => openModal(src)}
              tabIndex={0}
              style={{ cursor: "pointer" }}
              aria-label={`Gallery image ${i % images.length + 1}`}
              onKeyDown={e => (e.key === "Enter" || e.key === " ") && openModal(src)}
            >
              <img
                src={src}
                alt={`Golden retriever puppy ${i % images.length + 1}`}
                loading="lazy"
                draggable={false}
              />
            </div>
          ))}
        </motion.div>
      </div>
      {/* Lightbox Modal */}
      {modalOpen && (
        <div
          className="gallery-modal-backdrop"
          onClick={closeModal}
          tabIndex={-1}
          aria-modal="true"
          role="dialog"
        >
          <span
            className="gallery-modal-close"
            aria-label="Close"
            onClick={closeModal}
            tabIndex={0}
            onKeyDown={e => (e.key === "Enter" || e.key === " ") && closeModal()}
          >
            &times;
          </span>
          <img
            className="gallery-modal-img"
            src={modalImg}
            alt="Large preview"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
